import { useEffect, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { Popover } from '@mantine/core';

import { EButton, ECheckbox, EModal, EPlaceholder, EPopover, ESegmentedControl, ETextArea } from 'libs/ui';
import { showToast } from 'utils/utility';
import { useFlightData, useHotelData } from 'libs/data-access';
import { IFlightOnlineBookingList } from 'libs/data-access/types';

interface IProps {
    flightTicket: IFlightOnlineBookingList;
    handleRefresh: () => void;
};

export default function FlightRefundTicket({
    flightTicket,
    handleRefresh
}: IProps) {

    const { postFlightData } = useFlightData();

    const [refundPopover, { open: openRefundPopover, close: closeRefundPopover }] = useDisclosure(false);

    const [journeySegments, setJourneySegments] = useState<{
        label: string; value: string; disabled: boolean;
    }[]>([]);
    const [refundInformation, setRefundInformation] = useState<{
        remarks: string | null;
        selectedJourney: { bookingId: number; key: string; };
        isConfirmed: boolean;
    }>({
        remarks: null,
        selectedJourney: { bookingId: 0, key: "" },
        isConfirmed: false
    });
    const [isSaving, setIsSaving] = useState<boolean>(false);


    useEffect(() => {
        createJourneySegments();
    }, [flightTicket]);


    // HANDLER FUNCTIONS
    const createJourneySegments = () => {
        let segments: { label: string; value: string; disabled: boolean }[] = [];

        flightTicket.bookingResponses.forEach(
            booking => {
                // for galileo journeys
                booking.galileoBookingDetailResponses?.forEach(
                    galJourney => segments.push({
                        label: `${galJourney.origin} - ${galJourney.destination}`,
                        value: `${booking.id}/${galJourney.origin}_${galJourney.destination}`,
                        disabled: galJourney.bookingStatus !== "TO_BE_REFUNDED"
                    })
                );

                // for navitaire
                booking.navitaireBookingDetailResponses?.forEach(
                    navJourney => segments.push({
                        label: `${navJourney.origin} - ${navJourney.destination}`,
                        value: `${booking.id}/${navJourney.origin}_${navJourney.destination}`,
                        disabled: navJourney.bookingStatus !== "TO_BE_REFUNDED"
                    })
                )
            }
        );

        // console.log("segments: ", segments);

        setJourneySegments(segments);
    };


    // API CALLS
    const initiateRefund = async () => {
        if (!refundInformation.selectedJourney.bookingId && !refundInformation.selectedJourney.key) {
            showToast({
                type: "warning",
                message: "Please select a journey first!"
            });
            return;
        };

        if (!refundInformation.remarks) {
            showToast({
                type: "warning",
                message: "Please provide remarks!"
            });
            return;
        };

        if (!refundInformation.isConfirmed) {
            showToast({
                type: "warning",
                message: "Please click on the checkbox to confirm!"
            });
            return;
        };

        setIsSaving(true);

        const response = postFlightData(`/booking/${refundInformation.selectedJourney.bookingId}/refund-booking-amount?key=${refundInformation.selectedJourney.key}`, null);

        if ((await response).isSuccess) {
            // console.log("Response for refunding ticket: ", response);
            showToast({
                type: "success",
                message: "Refund initiated successfully!"
            });
            handleRefresh();
        } else {
            console.log("Error while refunding ticket: ", response);
            showToast({
                type: "error",
                message: (await response).message || "Error while refunding ticket!"
            });
        };

        setIsSaving(false);
    };


    return (
        <>
            <EPopover
                width={450}
                opened={refundPopover}
                onClose={() => {
                    closeRefundPopover();
                }}
            >
                <Popover.Target>
                    <div>
                        <EButton
                            size="sm"
                            variant="light"
                            color="red"
                            onClick={openRefundPopover}
                        >Refund</EButton>
                    </div>
                </Popover.Target>

                <Popover.Dropdown>
                    <div className="w-96 p-2">
                        <h3 className="text-lg font-medium mb-4">Mark as To Be Refunded</h3>
                        <div className="flex flex-col gap-6">
                            <ESegmentedControl
                                fullWidth
                                data={journeySegments}
                                value={`${refundInformation.selectedJourney.bookingId}/${refundInformation.selectedJourney.key}`}
                                onChange={(value) => setRefundInformation({
                                    remarks: null,
                                    selectedJourney: {
                                        bookingId: Number(value.split("/")[0]),
                                        key: value.split("/")[1]
                                    },
                                    isConfirmed: false
                                })}
                            />

                            {
                                refundInformation.selectedJourney.bookingId && refundInformation.selectedJourney.key
                                    ?
                                    <>
                                        <ETextArea
                                            required
                                            label="Remarks"
                                            placeholder="Please enter remarks"
                                            value={refundInformation.remarks ?? undefined}
                                            onChange={e => setRefundInformation(prev => ({ ...prev, remarks: e.target.value }))}
                                        />

                                        <ECheckbox
                                            label={
                                                <span>Are you sure you want to initiate refund for the selected journey <span className="font-semibold">{refundInformation.selectedJourney.key}</span>?</span>
                                            }
                                            onChange={(e) => setRefundInformation(prev => ({ ...prev, isConfirmed: e.target.checked }))}
                                        />
                                    </>
                                    :
                                    <EPlaceholder
                                        title="Please select a journey from above to initiate refund!"
                                    />
                            }

                            {
                                refundInformation.isConfirmed
                                    ?
                                    <div className="flex justify-end gap-2">
                                        <EButton
                                            variant="light"
                                            onClick={closeRefundPopover}
                                        >Cancel</EButton>
                                        <EButton
                                            onClick={initiateRefund}
                                        >Save</EButton>
                                    </div>
                                    :
                                    <></>
                            }
                        </div>
                    </div>
                </Popover.Dropdown>
            </EPopover>
        </>
    );

}